import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CAMADAS,
  CAMADAS_IDS,
  DOCUMENTOS,
  ENDERECO_VAZIO,
  MAX_PARCELAS_SALDO,
  MAX_TITULARES,
  formatarBRL,
  rateioPrevisto,
  restricoesDeEdicao,
  valoresVigentes,
  type Candidato,
  type CandidatoPatch,
  type ModalidadeCobranca,
  type TipoCobranca,
  type TipoDocumento,
  type Titular,
} from '@aponte/shared';
import { api, type DocumentoNaFicha, type FichaCompleta } from '../../api/cliente.js';
import { Aguarde } from '../../componentes/Aguarde.js';
import { Aviso, mensagemDoErro, type Mensagem } from '../../componentes/Aviso.js';
import { Campo } from '../../componentes/Campo.js';
import { Dialogo } from '../../componentes/Dialogo.js';
import { BlocoDeEndereco } from './BlocoDeEndereco.js';
import { ListaDeDocumentos } from './ListaDeDocumentos.js';
import { SeletorDeCadeira } from './SeletorDeCadeira.js';
import { SeloDeEtapa } from '../../componentes/SeloDeEtapa.js';
import { useSessao } from '../../autenticacao/ContextoDeSessao.js';
import { Esteira } from './Esteira.js';
import { LinhaDoTempo } from './LinhaDoTempo.js';

/** O que está sendo perguntado ao operador no momento. */
type Pergunta =
  | { tipo: 'recusa' }
  | { tipo: 'cobranca'; cobrancaTipo: TipoCobranca; modalidade: ModalidadeCobranca; valor: number }
  | { tipo: 'assinatura'; documento: DocumentoNaFicha }
  | { tipo: 'pendencia'; abrir: boolean }
  | { tipo: 'exclusao' };

const TITULAR_VAZIO: Titular = { nome: '', cargo: '', cpf: '', contato: '' };

/** Ponto de partida de uma ficha nova, antes de existir no servidor. */
const EM_BRANCO: CandidatoPatch = {
  tipo: 'pj',
  razao: '',
  doc: '',
  endereco: { ...ENDERECO_VAZIO },
  telefone: '',
  representante: '',
  email: '',
  titulares: Array.from({ length: MAX_TITULARES }, () => ({ ...TITULAR_VAZIO })),
  camada: 'movimento',
  numero: '',
  observacoes: '',
};

/** Ficha do candidato: cadastro, cadeira, esteira e trilha de auditoria. */
export function Ficha() {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const consultas = useQueryClient();
  const { temPapel } = useSessao();

  /**
   * Ficha nova é rascunho de tela: nada é gravado até o closer salvar.
   *
   * O protótipo criava o registro já no clique do botão — cabia lá, porque escrevia num
   * localStorage descartável. Aqui cada clique viraria registro persistido, evento de
   * auditoria e uma linha no funil, inclusive de quem abriu e desistiu.
   */
  const eNova = id === 'novo';

  const [rascunho, setRascunho] = useState<CandidatoPatch | null>(eNova ? EM_BRANCO : null);
  const [mensagem, setMensagem] = useState<Mensagem | null>(null);
  const [errosDeCampo, setErrosDeCampo] = useState<Record<string, string>>({});
  const [pergunta, setPergunta] = useState<Pergunta | null>(null);
  const [arquivoAssinado, setArquivoAssinado] = useState<File | null>(null);
  const [dispensarArquivo, setDispensarArquivo] = useState(false);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [avisoDoCnpj, setAvisoDoCnpj] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['ficha', id],
    queryFn: () => api.obterFicha(id),
    enabled: !eNova,
  });

  // Carrega a ficha no formulário quando ela chega (ou quando troca de candidato).
  useEffect(() => {
    if (consulta.data) setRascunho(paraRascunho(consulta.data));
  }, [consulta.data]);

  const recarregar = () => {
    void consultas.invalidateQueries({ queryKey: ['ficha', id] });
    void consultas.invalidateQueries({ queryKey: ['candidatos'] });
  };

  const tratarErro = (erro: unknown) => {
    const campos = (erro as { campos?: Record<string, string> }).campos;
    setErrosDeCampo(campos ?? {});
    setMensagem({ tom: 'erro', texto: mensagemDoErro(erro) });
  };

  const salvar = useMutation({
    mutationFn: (patch: CandidatoPatch) =>
      eNova
        ? api.criarCandidato(patch)
        : // A versão carregada vai no If-Match: se outra pessoa salvou nesse meio-tempo,
          // o servidor recusa em vez de apagar o trabalho dela.
          api.atualizarCandidato(id, patch, consulta.data?.candidato.atualizadoEm),
    onSuccess: (candidato) => {
      setErrosDeCampo({});
      void consultas.invalidateQueries({ queryKey: ['candidatos'] });

      if (eNova) {
        // Substitui a entrada no histórico para o botão "voltar" do navegador não
        // devolver o closer a uma ficha em branco que já virou registro.
        navegar(`/candidatos/${candidato.candidatoId}`, { replace: true });
        return;
      }

      setMensagem({ tom: 'ok', texto: 'Ficha salva.' });
      recarregar();
    },
    onError: tratarErro,
  });

  const emitir = useMutation({
    mutationFn: (tipo: TipoDocumento) => api.emitirDocumento(id, tipo),
    onSuccess: ({ documento, url }) => {
      setMensagem({
        tom: 'ok',
        texto: (
          <>
            <b>{DOCUMENTOS[documento.tipo].nome}</b> emitido — versão {documento.versao}.{' '}
            <a href={url} target="_blank" rel="noopener noreferrer">
              Abrir agora
            </a>
            <br />
            <span style={{ fontSize: 10.5, opacity: 0.7 }}>
              Fica guardado em <b>Documentos emitidos</b>, logo abaixo, e pode ser reaberto a
              qualquer momento. SHA-256 {documento.hash.slice(0, 32)}…
            </span>
          </>
        ),
      });
      recarregar();
    },
    onError: tratarErro,
  });

  const cobrar = useMutation({
    mutationFn: ({ tipo, modalidade }: { tipo: TipoCobranca; modalidade: ModalidadeCobranca }) =>
      api.gerarCobranca(id, { tipo, modalidade }),
    onSuccess: (cobranca) => {
      setMensagem({
        tom: 'ok',
        texto: (
          <>
            <b>Link gerado</b> — {formatarBRL(cobranca.valor)}
            {/*
             * "até 12x", não "12x": o link só oferece o teto. Quantas parcelas de fato
             * saem é decisão do pagador, e só se sabe quando ele paga.
             */}
            {cobranca.modalidade === 'parcelado'
              ? ` em até ${cobranca.maxParcelas}x no cartão`
              : ''}
            {cobranca.valorSplitAcademia > 0 ? (
              <>, com split de {formatarBRL(cobranca.valorSplitAcademia)} para a Academia.</>
            ) : (
              /*
               * O motivo (ambiente sem split, ou carteira da Academia ainda não
               * cadastrada) fica na trilha. Aqui interessa a consequência, que é a mesma
               * nos dois casos e é o que o operador precisa saber antes de mandar o link.
               */
              <>
                , <b>sem rateio automático</b> — o valor entra inteiro na conta da RREVELA e a parte
                da Academia precisa de repasse manual.
              </>
            )}
            <br />
            <a href={cobranca.urlFatura} target="_blank" rel="noopener noreferrer">
              {cobranca.urlFatura}
            </a>
          </>
        ),
      });
      recarregar();
    },
    onError: tratarErro,
  });

  const mudarEtapa = useMutation({
    mutationFn: ({
      etapa,
      motivo,
    }: {
      etapa: 'aprovado' | 'recusado' | 'aceite' | 'ativo';
      motivo?: string;
    }) => api.mudarEtapa(id, etapa, motivo),
    onSuccess: (candidato) => {
      setMensagem(
        candidato.etapa === 'recusado'
          ? {
              tom: 'erro',
              texto: (
                <>
                  Não aprovado pela curadoria.{' '}
                  <b>Providencie a devolução integral do sinal em até 5 dias úteis</b>, pelo mesmo
                  meio de pagamento (Carta, cl. 5.2).
                </>
              ),
            }
          : {
              tom: 'ok',
              texto:
                candidato.etapa === 'aprovado'
                  ? 'Aprovado pela curadoria. Termo de Aceite e Regimento liberados para emissão.'
                  : 'Etapa atualizada.',
            },
      );
      recarregar();
    },
    onError: tratarErro,
  });

  const assinar = useMutation({
    mutationFn: ({
      documento,
      arquivo,
      motivo,
    }: {
      documento: DocumentoNaFicha;
      arquivo: File | null;
      motivo?: string;
    }) => api.registrarAssinatura(id, documento.documentoId, arquivo, motivo),
    onSuccess: ({ documento }) => {
      setPergunta(null);
      setArquivoAssinado(null);
      setDispensarArquivo(false);
      setMensagem({
        tom: 'ok',
        texto: `${DOCUMENTOS[documento.tipo].nome} assinado registrado. A esteira seguiu.`,
      });
      recarregar();
    },
    onError: tratarErro,
  });

  const pendencia = useMutation({
    mutationFn: ({ abrir, motivo }: { abrir: boolean; motivo: string }) =>
      api.abrirOuFecharPendencia(id, abrir ? motivo : null, motivo),
    onSuccess: () => {
      setPergunta(null);
      setMensagem({ tom: 'ok', texto: 'Pendência financeira atualizada.' });
      recarregar();
    },
    onError: tratarErro,
  });

  const excluir = useMutation({
    mutationFn: () => api.excluirCandidato(id),
    onSuccess: () => {
      consultas.invalidateQueries({ queryKey: ['candidatos'] });
      navegar('/');
    },
    onError: tratarErro,
  });

  if (!rascunho || (!eNova && consulta.isLoading)) {
    return <div className="carregando">Carregando a ficha…</div>;
  }

  if (consulta.isError) {
    return <Aviso mensagem={{ tom: 'erro', texto: mensagemDoErro(consulta.error) }} />;
  }

  const ficha = consulta.data ?? null;
  const candidato = ficha?.candidato ?? null;
  const pf = rascunho.tipo === 'pf';
  const camada = rascunho.camada ?? 'movimento';
  // Mostra o que está contratado, não o que a tabela diz hoje.
  const valores = valoresVigentes(candidato ?? { camada, valoresContratados: undefined });
  const ocupado =
    salvar.isPending ||
    emitir.isPending ||
    cobrar.isPending ||
    mudarEtapa.isPending ||
    assinar.isPending ||
    pendencia.isPending;
  const somenteLeitura = !temPapel('closer');

  /*
   * Campos que a esteira já congelou. A trava real é do servidor; aqui o campo aparece
   * desabilitado com o motivo, para o operador não descobrir no erro depois de digitar.
   */
  const restricoes = candidato ? restricoesDeEdicao(candidato) : [];
  const congelado = (campo: keyof CandidatoPatch) => restricoes.find((r) => r.campo === campo);

  const alterar = (mudanca: CandidatoPatch) => setRascunho((atual) => ({ ...atual, ...mudanca }));

  /**
   * Puxa os dados cadastrais do CNPJ e preenche o que estiver em branco.
   *
   * Não sobrescreve o que a pessoa já digitou: a base da Receita traz a razão social
   * formal e o endereço da matriz, que nem sempre é o que vale para o contrato. O que ela
   * corrigiu à mão continua valendo.
   */
  async function buscarPeloCnpj(doc: string) {
    if (doc.length !== 14 || somenteLeitura) return;

    setBuscandoCnpj(true);
    setAvisoDoCnpj(null);
    try {
      const achado = await api.consultarCnpj(doc);
      setRascunho((atual) => {
        if (!atual) return atual;
        const enderecoAtual = atual.endereco ?? ENDERECO_VAZIO;
        return {
          ...atual,
          razao: atual.razao || achado.razaoSocial,
          telefone: atual.telefone || achado.telefone,
          email: atual.email || achado.email,
          endereco: {
            cep: enderecoAtual.cep || achado.endereco.cep,
            logradouro: enderecoAtual.logradouro || achado.endereco.logradouro,
            numero: enderecoAtual.numero || achado.endereco.numero,
            complemento: enderecoAtual.complemento || achado.endereco.complemento,
            bairro: enderecoAtual.bairro || achado.endereco.bairro,
            cidade: enderecoAtual.cidade || achado.endereco.cidade,
            uf: enderecoAtual.uf || achado.endereco.uf,
          },
        };
      });

      const irregular = achado.situacao && !/ativa/i.test(achado.situacao);
      setAvisoDoCnpj(
        irregular
          ? `Atenção: situação cadastral "${achado.situacao}".`
          : `${achado.razaoSocial} · ${achado.situacao || 'consultado'}. Só os campos vazios foram preenchidos.`,
      );
    } catch (erro) {
      setAvisoDoCnpj(mensagemDoErro(erro));
    } finally {
      setBuscandoCnpj(false);
    }
  }

  const alterarTitular = (indice: number, mudanca: Partial<Titular>) => {
    const titulares = [...(rascunho.titulares ?? [])];
    while (titulares.length < MAX_TITULARES) titulares.push({ ...TITULAR_VAZIO });
    titulares[indice] = { ...titulares[indice]!, ...mudanca };
    alterar({ titulares });
  };

  return (
    <>
      <button className="voltar" onClick={() => navegar('/')}>
        ← Voltar ao funil
      </button>

      <div className="secao" style={{ marginTop: 4 }}>
        <h2>
          {candidato?.razao || (
            <>
              Novo <em>candidato</em>
            </>
          )}
        </h2>
        {candidato ? (
          <SeloDeEtapa etapa={candidato.etapa} />
        ) : (
          <span className="selo neutro">Rascunho · ainda não salvo</span>
        )}
      </div>

      {candidato?.pendenciaFinanceira && (
        <div className="tarja-pendencia">
          <b>Pendência financeira</b>
          {candidato.pendenciaFinanceira}
          {temPapel('admin') && (
            <div style={{ marginTop: 10 }}>
              <button
                className="bt vazado pequeno"
                onClick={() => setPergunta({ tipo: 'pendencia', abrir: false })}
              >
                Encerrar pendência
              </button>
            </div>
          )}
        </div>
      )}

      <fieldset>
        <legend>1 · Empresa ou pessoa</legend>

        <div className="tipo-pessoa">
          {(['pj', 'pf'] as const).map((tipo) => (
            <label key={tipo}>
              <input
                type="radio"
                name="tipo"
                value={tipo}
                checked={rascunho.tipo === tipo}
                disabled={somenteLeitura || !!congelado('tipo')}
                onChange={() =>
                  // Dado de PJ não pode sobreviver numa ficha de PF: hoje os templates
                  // filtram por tipo, mas dado morto guardado é vazamento esperando template
                  // novo. Some da ficha junto com o tipo.
                  alterar(
                    tipo === 'pf'
                      ? {
                          tipo,
                          representante: '',
                          titulares: Array.from({ length: MAX_TITULARES }, () => ({
                            ...TITULAR_VAZIO,
                          })),
                        }
                      : { tipo },
                  )
                }
              />
              {tipo === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
            </label>
          ))}
        </div>

        <div className="linha">
          <Campo
            rotulo={pf ? 'Nome completo' : 'Razão social'}
            valor={rascunho.razao ?? ''}
            aoMudar={(razao) => alterar({ razao })}
            placeholder={pf ? 'Nome do proponente' : 'Empresa Ltda.'}
            erro={errosDeCampo.razao}
            dica={congelado('razao')?.motivo}
            somenteLeitura={somenteLeitura || !!congelado('razao')}
          />
          <Campo
            rotulo={pf ? 'CPF' : 'CNPJ'}
            valor={rascunho.doc ?? ''}
            mascara={pf ? 'cpf' : 'cnpj'}
            aoMudar={(doc) => alterar({ doc })}
            placeholder={pf ? '000.000.000-00' : '00.000.000/0001-00'}
            erro={errosDeCampo.doc}
            somenteLeitura={somenteLeitura || !!congelado('doc')}
            dica={congelado('doc')?.motivo ?? (!pf && avisoDoCnpj ? avisoDoCnpj : undefined)}
            acao={
              pf ? undefined : (
                <button
                  type="button"
                  className="bt vazado pequeno"
                  disabled={somenteLeitura || buscandoCnpj || (rascunho.doc ?? '').length !== 14}
                  onClick={() => void buscarPeloCnpj(rascunho.doc ?? '')}
                >
                  {buscandoCnpj ? '…' : 'Buscar'}
                </button>
              )
            }
          />
        </div>

        <div className="linha">
          <Campo
            rotulo="Telefone"
            valor={rascunho.telefone ?? ''}
            mascara="telefone"
            aoMudar={(telefone) => alterar({ telefone })}
            placeholder="(11) 3000-0000"
            erro={errosDeCampo.telefone}
            somenteLeitura={somenteLeitura}
          />
          <Campo
            rotulo="E-mail"
            tipo="email"
            valor={rascunho.email ?? ''}
            aoMudar={(email) => alterar({ email })}
            placeholder="nome@empresa.com.br"
            erro={errosDeCampo.email}
            somenteLeitura={somenteLeitura}
          />
        </div>

        {!pf && (
          <div className="linha">
            <Campo
              rotulo="Representante legal"
              valor={rascunho.representante ?? ''}
              aoMudar={(representante) => alterar({ representante })}
              placeholder="Nome do responsável"
              erro={errosDeCampo.representante}
              somenteLeitura={somenteLeitura}
            />
          </div>
        )}

        <BlocoDeEndereco
          endereco={rascunho.endereco ?? ENDERECO_VAZIO}
          somenteLeitura={somenteLeitura}
          aoMudar={(endereco) => alterar({ endereco })}
        />
      </fieldset>

      {!pf && (
        <fieldset>
          <legend>2 · Titulares da cadeira · até {MAX_TITULARES}</legend>
          <div className="dica" style={{ margin: '0 0 14px' }}>
            Aplica-se somente a cadeiras de pessoa jurídica. Na pessoa física, o uso é pessoal e
            intransferível.
          </div>

          {Array.from({ length: MAX_TITULARES }, (_, i) => {
            const titular = rascunho.titulares?.[i] ?? TITULAR_VAZIO;
            return (
              <div className="titular" key={i}>
                <div className="cabecalho">
                  <b>Titular {String(i + 1).padStart(2, '0')}</b>
                </div>
                <div className="linha t4">
                  <Campo
                    rotulo="Nome completo"
                    valor={titular.nome}
                    aoMudar={(nome) => alterarTitular(i, { nome })}
                    somenteLeitura={somenteLeitura}
                  />
                  <Campo
                    rotulo="Cargo"
                    valor={titular.cargo}
                    aoMudar={(cargo) => alterarTitular(i, { cargo })}
                    somenteLeitura={somenteLeitura}
                  />
                  <Campo
                    rotulo="CPF"
                    valor={titular.cpf}
                    mascara="cpf"
                    aoMudar={(cpf) => alterarTitular(i, { cpf })}
                    erro={errosDeCampo[`titulares.${i}.cpf`]}
                    somenteLeitura={somenteLeitura}
                  />
                  <Campo
                    rotulo="Telefone e e-mail"
                    valor={titular.contato}
                    aoMudar={(contato) => alterarTitular(i, { contato })}
                    somenteLeitura={somenteLeitura}
                  />
                </div>
              </div>
            );
          })}
        </fieldset>
      )}

      <fieldset>
        <legend>3 · Cadeira</legend>
        <div className="linha t3">
          <div className="campo">
            <label htmlFor="camada">Camada</label>
            <select
              id="camada"
              value={camada}
              disabled={somenteLeitura || !!congelado('camada')}
              onChange={(e) => {
                // Cada camada tem sua faixa na numeração corrida de 001 a 100, então a
                // cadeira antiga nunca serve na camada nova — e guardá-la só garantiria a
                // recusa do servidor ao salvar. É rascunho: nada foi gravado ainda.
                const nova = e.target.value as typeof camada;
                alterar({ camada: nova, numero: '' });
              }}
            >
              {CAMADAS_IDS.map((c) => (
                <option key={c} value={c}>
                  {CAMADAS[c].nome} · {formatarBRL(CAMADAS[c].valor)}
                </option>
              ))}
            </select>
          </div>
          <SeletorDeCadeira
            camada={camada}
            valor={rascunho.numero ?? ''}
            aoMudar={(numero) => alterar({ numero })}
            candidatoId={candidato?.candidatoId}
            erro={errosDeCampo.numero}
            dica={congelado('numero')?.motivo}
            somenteLeitura={somenteLeitura || !!congelado('numero')}
          />
          <div className="campo">
            <label htmlFor="referencia">Referência interna</label>
            <input
              id="referencia"
              value={candidato?.referencia ?? 'gerada ao salvar'}
              readOnly
              disabled
            />
            <div className="dica">Sequencial do ano, atribuído pelo sistema.</div>
          </div>
        </div>

        <div className="resumo-valores">
          <div>
            <div className="chave">Investimento anual</div>
            <div className="valor">{formatarBRL(valores.total)}</div>
          </div>
          <div>
            <div className="chave">Sinal · 5% · à vista</div>
            <div className="valor">{formatarBRL(valores.sinal)}</div>
          </div>
          <div>
            <div className="chave">Saldo · 95%</div>
            <div className="valor">{formatarBRL(valores.saldo)}</div>
          </div>
        </div>

        <div className="dica" style={{ marginTop: 12 }}>
          Vigência de <b>12 meses contados do aceite do comitê</b>. Split de 70% Academia e 30%
          RREVELA sobre o valor líquido, aplicado pelo gateway.
        </div>
      </fieldset>

      {candidato && ficha ? (
        <fieldset>
          <legend>4 · Esteira</legend>
          <Esteira
            candidato={candidato}
            cobrancas={ficha.cobrancas}
            documentos={ficha.documentos}
            ocupado={ocupado}
            aoEmitir={(tipo) => emitir.mutate(tipo)}
            aoRegistrarAssinatura={(documento) => setPergunta({ tipo: 'assinatura', documento })}
            aoCobrar={(cobrancaTipo, modalidade) => {
              // Cobrança de seis dígitos não sai no primeiro clique: o operador confere
              // valor, modalidade e destinatário antes de o link existir de verdade.
              const v = valoresVigentes(candidato);
              setPergunta({
                tipo: 'cobranca',
                cobrancaTipo,
                modalidade,
                valor: cobrancaTipo === 'sinal' ? v.sinal : v.saldo,
              });
            }}
            aoDecidirCuradoria={(aprovado) => {
              if (aprovado) {
                mudarEtapa.mutate({ etapa: 'aprovado' });
                return;
              }
              setPergunta({ tipo: 'recusa' });
            }}
            aoMarcarAceite={() => mudarEtapa.mutate({ etapa: 'aceite' })}
            aoRegistrarOnboarding={() => mudarEtapa.mutate({ etapa: 'ativo' })}
          />
          <Aviso mensagem={mensagem} />
        </fieldset>
      ) : (
        <fieldset>
          <legend>4 · Esteira</legend>
          <div className="aguardando">
            Salve o candidato para liberar a esteira. Carta de Intenções, cobranças e documentos
            precisam de uma ficha existente para ficarem vinculados a ela.
          </div>
          <Aviso mensagem={mensagem} />
        </fieldset>
      )}

      {ficha && candidato && (
        <fieldset>
          <legend>5 · Documentos emitidos</legend>
          <ListaDeDocumentos candidatoId={candidato.candidatoId} documentos={ficha.documentos} />
        </fieldset>
      )}

      {ficha && (
        <fieldset>
          <legend>6 · Histórico</legend>
          <LinhaDoTempo eventos={ficha.eventos} />
        </fieldset>
      )}

      <div style={{ display: 'flex', gap: 11, marginTop: 20, flexWrap: 'wrap' }}>
        {!somenteLeitura && (
          <button className="bt" disabled={ocupado} onClick={() => salvar.mutate(rascunho)}>
            {salvar.isPending ? 'Salvando…' : eNova ? 'Criar candidato' : 'Salvar candidato'}
          </button>
        )}
        <button className="bt vazado" onClick={() => navegar('/')}>
          {eNova ? 'Descartar' : 'Voltar'}
        </button>
        {candidato && temPapel('admin') && (
          <button
            className="bt perigo pequeno"
            style={{ marginLeft: 'auto' }}
            disabled={excluir.isPending}
            onClick={() => setPergunta({ tipo: 'exclusao' })}
          >
            Excluir
          </button>
        )}
      </div>

      {emitir.isPending && (
        <Aguarde
          titulo="Gerando o documento"
          descricao="Isso leva alguns segundos."
          etapas={[{ apos: 20, texto: 'Está demorando mais que o normal. Aguarde mais um pouco.' }]}
        />
      )}

      {cobrar.isPending && (
        <Aguarde
          titulo="Gerando o link de pagamento"
          descricao="Isso leva alguns segundos."
          etapas={[{ apos: 20, texto: 'Está demorando mais que o normal. Não clique de novo.' }]}
        />
      )}

      {assinar.isPending && (
        <Aguarde
          titulo="Registrando a assinatura"
          descricao="Isso leva alguns segundos."
          etapas={[{ apos: 20, texto: 'Está demorando mais que o normal. Aguarde mais um pouco.' }]}
        />
      )}

      {pergunta && (
        <PerguntaAoOperador
          pergunta={pergunta}
          candidato={candidato}
          ocupado={ocupado}
          arquivoAssinado={arquivoAssinado}
          aoEscolherArquivo={setArquivoAssinado}
          dispensarArquivo={dispensarArquivo}
          aoDispensarArquivo={setDispensarArquivo}
          podeDispensarArquivo={temPapel('admin')}
          aoCancelar={() => {
            setPergunta(null);
            setArquivoAssinado(null);
            setDispensarArquivo(false);
          }}
          aoConfirmar={(texto) => {
            if (pergunta.tipo === 'recusa') {
              mudarEtapa.mutate({ etapa: 'recusado', motivo: texto });
              setPergunta(null);
            } else if (pergunta.tipo === 'cobranca') {
              cobrar.mutate({ tipo: pergunta.cobrancaTipo, modalidade: pergunta.modalidade });
              setPergunta(null);
            } else if (pergunta.tipo === 'assinatura') {
              assinar.mutate({
                documento: pergunta.documento,
                arquivo: arquivoAssinado,
                motivo: dispensarArquivo ? texto : undefined,
              });
            } else if (pergunta.tipo === 'pendencia') {
              pendencia.mutate({ abrir: pergunta.abrir, motivo: texto });
            } else {
              excluir.mutate();
              setPergunta(null);
            }
          }}
        />
      )}
    </>
  );
}

interface PerguntaProps {
  pergunta: Pergunta;
  candidato: Candidato | null;
  ocupado: boolean;
  arquivoAssinado: File | null;
  aoEscolherArquivo: (arquivo: File | null) => void;
  dispensarArquivo: boolean;
  aoDispensarArquivo: (dispensar: boolean) => void;
  podeDispensarArquivo: boolean;
  aoConfirmar: (texto: string) => void;
  aoCancelar: () => void;
}

/** Todos os diálogos da ficha num lugar só, para o corpo do componente não inchar. */
function PerguntaAoOperador({
  pergunta,
  candidato,
  ocupado,
  arquivoAssinado,
  aoEscolherArquivo,
  dispensarArquivo,
  aoDispensarArquivo,
  podeDispensarArquivo,
  aoConfirmar,
  aoCancelar,
}: PerguntaProps) {
  if (pergunta.tipo === 'recusa') {
    return (
      <Dialogo
        titulo="Não aprovar na curadoria"
        rotuloConfirmar="Confirmar recusa"
        tomConfirmar="perigo"
        campo={{
          rotulo: 'Motivo da não aprovação',
          obrigatorio: true,
          minimo: 10,
          placeholder: 'Fica registrado na ficha e sustenta a devolução do sinal.',
        }}
        ocupado={ocupado}
        aoConfirmar={aoConfirmar}
        aoCancelar={aoCancelar}
      >
        A não aprovação obriga a <b>devolução integral do sinal em até 5 dias úteis</b>, pelo mesmo
        meio de pagamento (Carta de Intenções, cl. 5.2).
      </Dialogo>
    );
  }

  if (pergunta.tipo === 'cobranca') {
    const rateio = rateioPrevisto(pergunta.valor);
    return (
      <Dialogo
        titulo={`Gerar cobrança de ${pergunta.cobrancaTipo}`}
        rotuloConfirmar="Gerar cobrança"
        ocupado={ocupado}
        aoConfirmar={aoConfirmar}
        aoCancelar={aoCancelar}
      >
        Será criada uma cobrança <b>real</b> no gateway, em nome de{' '}
        <b>{candidato?.razao || 'este candidato'}</b>.
        <br />
        <br />
        Valor: <b>{formatarBRL(pergunta.valor)}</b>
        <br />
        {pergunta.modalidade === 'parcelado' ? (
          <>
            Pagamento: <b>cartão de crédito</b>, e o pagador escolhe de 1x a{' '}
            <b>{MAX_PARCELAS_SALDO}x</b> (a partir de{' '}
            {formatarBRL(Math.round(pergunta.valor / MAX_PARCELAS_SALDO))} por parcela).
          </>
        ) : (
          <>
            Pagamento: <b>à vista</b>, no cartão ou por Pix.
          </>
        )}
        <br />
        Rateio previsto: {formatarBRL(rateio.academia)} Academia · {formatarBRL(rateio.rrevela)}{' '}
        RREVELA
      </Dialogo>
    );
  }

  if (pergunta.tipo === 'assinatura') {
    return (
      <Dialogo
        titulo={`Registrar ${DOCUMENTOS[pergunta.documento.tipo].nome} assinado`}
        rotuloConfirmar="Registrar assinatura"
        ocupado={ocupado}
        podeConfirmar={!!arquivoAssinado || dispensarArquivo}
        campo={
          dispensarArquivo
            ? {
                rotulo: 'Justificativa da dispensa',
                obrigatorio: true,
                minimo: 10,
                placeholder: 'Ex.: via assinada em papel, arquivada na secretaria, a digitalizar.',
              }
            : undefined
        }
        aoConfirmar={aoConfirmar}
        aoCancelar={aoCancelar}
      >
        É <b>este registro</b> — e não a emissão do PDF — que libera o aceite e a cobrança do saldo.
        Por isso o arquivo assinado é obrigatório: sem ele, o sistema estaria afirmando uma
        aceitação que não pode comprovar.
        <div className="arquivo-assinado" style={{ marginTop: 14 }}>
          <label htmlFor="pdf-assinado">PDF assinado</label>
          <input
            id="pdf-assinado"
            type="file"
            accept="application/pdf"
            disabled={dispensarArquivo}
            onChange={(e) => aoEscolherArquivo(e.target.files?.[0] ?? null)}
          />
          {arquivoAssinado && <div className="dica">{arquivoAssinado.name}</div>}
        </div>
        {podeDispensarArquivo && (
          <label className="opcao-dispensa">
            <input
              type="checkbox"
              checked={dispensarArquivo}
              onChange={(e) => {
                aoDispensarArquivo(e.target.checked);
                if (e.target.checked) aoEscolherArquivo(null);
              }}
            />
            Registrar sem o arquivo, com justificativa — fica marcado na trilha
          </label>
        )}
      </Dialogo>
    );
  }

  if (pergunta.tipo === 'pendencia') {
    return (
      <Dialogo
        titulo={pergunta.abrir ? 'Abrir pendência financeira' : 'Encerrar pendência financeira'}
        rotuloConfirmar={pergunta.abrir ? 'Abrir pendência' : 'Encerrar pendência'}
        tomConfirmar={pergunta.abrir ? 'perigo' : 'normal'}
        campo={{
          rotulo: pergunta.abrir ? 'Descreva a pendência' : 'Como foi resolvida',
          obrigatorio: true,
          minimo: 10,
          placeholder: pergunta.abrir
            ? 'Ex.: chargeback do sinal em disputa.'
            : 'Ex.: valor reembolsado e cadeira liberada em 11/08.',
        }}
        ocupado={ocupado}
        aoConfirmar={aoConfirmar}
        aoCancelar={aoCancelar}
      >
        Enquanto houver pendência, novas cobranças e a ocupação da cadeira ficam bloqueadas.
      </Dialogo>
    );
  }

  return (
    <Dialogo
      titulo="Excluir candidato"
      rotuloConfirmar="Excluir definitivamente"
      tomConfirmar="perigo"
      ocupado={ocupado}
      aoConfirmar={aoConfirmar}
      aoCancelar={aoCancelar}
    >
      A ficha, as cobranças e a linha do tempo saem do funil e <b>não podem ser recuperadas</b>. Os
      PDFs já emitidos permanecem guardados.
    </Dialogo>
  );
}

/** Só os campos editáveis viram rascunho — o resto é estado do servidor. */
function paraRascunho(ficha: FichaCompleta): CandidatoPatch {
  const c = ficha.candidato;
  const titulares = [...c.titulares];
  while (titulares.length < MAX_TITULARES) titulares.push({ ...TITULAR_VAZIO });

  return {
    tipo: c.tipo,
    razao: c.razao,
    doc: c.doc,
    endereco: { ...c.endereco },
    telefone: c.telefone,
    representante: c.representante,
    email: c.email,
    titulares,
    camada: c.camada,
    numero: c.numero,
    observacoes: c.observacoes,
  };
}
